import { useTranslation } from 'react-i18next';
import {
  InfiosChip,
  InfiosContainer,
  InfiosLink,
  InfiosTable,
  InfiosTableCell,
  InfiosTableColumn,
  InfiosTableHeaderCell,
  InfiosTextBlock,
} from '@infios/uikit/react';
import type { OrderSearchItem, OrderSearchResultsData } from '../../interfaces/chat';
import { useReferenceAction } from '../../context/ReferenceActionContext';
import { useComposeMessage } from '../../context/ComposeMessageContext';
import { FormattedDateTime } from '../FormattedDateTime';
import { getStatusChipColor } from './statusChipColor';
import { getStatusLabel } from './statusLabel';
import styles from './ContentBlocks.module.css';

interface OrderSearchResultsBlockProps {
  data: OrderSearchResultsData;
}

interface SearchRow extends OrderSearchItem {
  id: string;
}

export function OrderSearchResultsBlock({ data }: OrderSearchResultsBlockProps) {
  const { t } = useTranslation();
  const onReference = useReferenceAction();
  const composeMessage = useComposeMessage();
  const items = Array.isArray(data.items) ? data.items : [];
  if (items.length === 0) return null;

  const rows: SearchRow[] = items.map((item, idx) => ({
    ...item,
    id: item.orderId || item.orderNumber || String(idx),
  }));

  const selectionMode = data.selectionMode ?? 'single';

  // Multi-select: every check/uncheck refreshes the composer with all
  // currently-selected order numbers, replacing whatever's typed — same
  // contract as ComposeMessageContext documents for opt-in blocks.
  const handleSelectionChange = (e: CustomEvent) => {
    const detail = e.detail as { value?: { selectedRowIds?: string[] } };
    const selectedIds = new Set(detail.value?.selectedRowIds ?? []);
    const orderNumbers = rows
      .filter((r) => selectedIds.has(r.id))
      .map((r) => r.orderNumber)
      .filter((n): n is string => Boolean(n));
    if (orderNumbers.length > 0) {
      composeMessage?.(
        t('ChatbotUI.OrderBlocks.MultiOrderDetailsRequest', { orderNumbers: orderNumbers.join(', ') })
      );
    }
  };

  // Single mode: the order number itself is the selection affordance —
  // no radio button needed. Clicking it both opens the order reference and
  // fills the composer with a standard details inquiry for that order.
  const renderOrderNumber = (row: SearchRow) => {
    const refId = row.orderId || row.orderNumber;
    if (!refId || !row.orderNumber) return row.orderNumber ?? '—';
    return (
      <InfiosLink
        onClick={() => {
          onReference?.({ type: 'order', id: refId, action: 'open', label: row.orderNumber });
          if (selectionMode === 'single') {
            composeMessage?.(t('ChatbotUI.OrderBlocks.OrderDetailsRequest', { orderNumber: row.orderNumber }));
          }
        }}
      >
        {row.orderNumber}
      </InfiosLink>
    );
  };

  const renderStatus = (row: SearchRow) => {
    if (!row.status) return '—';
    return (
      <InfiosChip size="compact" {...getStatusChipColor(row.status)}>
        {getStatusLabel(row.status, t)}
      </InfiosChip>
    );
  };

  const renderOrderDate = (row: SearchRow) => <FormattedDateTime iso={row.orderDate} />;

  // Different search results carry one or the other of these two date
  // fields, never both — show whichever is present per row.
  const renderDelivery = (row: SearchRow) => (
    <FormattedDateTime iso={row.promisedDeliveryDate ?? row.expectedDeliveryDate} />
  );

  return (
    <InfiosContainer type="card">
      <InfiosTextBlock slot="header" weight="bold">
        {t('ChatbotUI.OrderBlocks.SearchResultsHeading')}
      </InfiosTextBlock>

      <InfiosTable
        dataSource={rows}
        checkbox={selectionMode === 'multi'}
        onInfiosRowSelectionChange={selectionMode === 'multi' ? handleSelectionChange : undefined}
      >
        <InfiosTableColumn columnId="orderNumber">
          <InfiosTableHeaderCell>{t('ChatbotUI.OrderBlocks.OrderNumber')}</InfiosTableHeaderCell>
          <InfiosTableCell renderer={renderOrderNumber} />
        </InfiosTableColumn>
        <InfiosTableColumn columnId="status">
          <InfiosTableHeaderCell>{t('ChatbotUI.OrderBlocks.Status')}</InfiosTableHeaderCell>
          <InfiosTableCell renderer={renderStatus} />
        </InfiosTableColumn>
        <InfiosTableColumn columnId="channel">
          <InfiosTableHeaderCell>{t('ChatbotUI.OrderBlocks.Channel')}</InfiosTableHeaderCell>
          <InfiosTableCell>{'{{row.channel}}'}</InfiosTableCell>
        </InfiosTableColumn>
        <InfiosTableColumn columnId="orderDate">
          <InfiosTableHeaderCell>{t('ChatbotUI.OrderBlocks.OrderDate')}</InfiosTableHeaderCell>
          <InfiosTableCell renderer={renderOrderDate} />
        </InfiosTableColumn>
        <InfiosTableColumn columnId="delivery">
          <InfiosTableHeaderCell>{t('ChatbotUI.OrderBlocks.ExpectedDelivery')}</InfiosTableHeaderCell>
          <InfiosTableCell renderer={renderDelivery} />
        </InfiosTableColumn>
      </InfiosTable>

      {data.hasMore ? <p className={styles.row}>{t('ChatbotUI.OrderBlocks.SearchResultsMore')}</p> : null}
    </InfiosContainer>
  );
}
